import { useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from "react"
import {
  AgentChatPanel,
  type AgentChatPanelProps,
  type AgentEntityUpdatedPayload,
} from "@/components/agent/agent-chat-panel"
import { WorkspaceBottomPanelChrome } from "@/components/workspace/workspace-bottom-panel-chrome"
import type { FloatingTabPillTab } from "@/components/ui/floating-tab-pill"
import type { AgentContextType } from "@/lib/agent-api"
import { useShellDrawerContentRight } from "@/hooks/use-shell-drawer-content-right"
import { useSidebarCollapsed, useUiStore } from "@/stores/ui-store"
import { cn } from "@/lib/utils"

export const WORKSPACE_BOTTOM_DOCK_CARD_CLASS =
  "relative overflow-visible rounded-lg border border-ui-border p-3 shadow-[0_-8px_40px_-8px_rgba(0,0,0,0.45)] ring-1 ring-black/5 backdrop-blur-md dark:bg-zinc-950/80 dark:shadow-2xl dark:shadow-black/40 light:bg-ui-bg-elevated/88 light:shadow-[0_-4px_24px_rgba(0,0,0,0.12)]"

export const WORKSPACE_BOTTOM_DOCK_COLLAPSE_TRANSITION = { type: "tween", duration: 0.12, ease: "linear" } as const

export type EntityAssistantContextType = Extract<AgentContextType, "character" | "location" | "prop">

export type WorkspaceBottomDockAgentProps = Omit<
  AgentChatPanelProps,
  | "expanded"
  | "onExpandedChange"
  | "anchor"
  | "showExpandToggle"
  | "collapseOnOutsideClick"
  | "fillAvailableHeight"
  | "chromeless"
  | "composerSlot"
  | "composerEdgeOverlay"
  | "zIndexClass"
  | "overlay"
  | "className"
  | "toolbar"
>

export type WorkspaceBottomDockChildrenCtx = {
  collapsed: boolean
}

type WorkspaceBottomDockProps = {
  tabs: FloatingTabPillTab[]
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  assistantActive: boolean
  agent: WorkspaceBottomDockAgentProps
  /** Non-assistant card body; receives collapse so content can keep a compact textarea strip. */
  children: (ctx: WorkspaceBottomDockChildrenCtx) => ReactNode
  toolbar?: ReactNode
  dirtyAriaLabel?: string
  /** Content above the dock card (e.g. Canvas reference picker). */
  aboveSlot?: ReactNode
  panelMountRef?: Ref<HTMLDivElement>
  /** Fixed dock root, so a page can measure the panel and keep content above it. */
  rootRef?: Ref<HTMLDivElement>
  className?: string
}

export function quickSurfaceForEntityContext(
  contextType: EntityAssistantContextType,
): "characters" | "locations" | "props" {
  if (contextType === "location") return "locations"
  if (contextType === "prop") return "props"
  return "characters"
}

type EntityBottomDockTab = "profile" | "assistant"

/** Tab / collapse / profile-dirty session for Characters / Locations / Props docks. */
export function useEntityBottomDockSession(
  selectedEntityId: number | null,
  defaultTab: EntityBottomDockTab = "profile",
) {
  const [tab, setTab] = useState<EntityBottomDockTab>(defaultTab)
  const [profileDirty, setProfileDirty] = useState(false)
  const collapsed = useUiStore((s) => s.workspaceBottomDockCollapsed)
  const setCollapsed = useUiStore((s) => s.setWorkspaceBottomDockCollapsed)

  useEffect(() => {
    setTab(defaultTab)
    setProfileDirty(false)
  }, [selectedEntityId, defaultTab])

  const selectTab = (next: EntityBottomDockTab) => {
    setTab(next)
    if (next === "profile") setProfileDirty(false)
  }

  const handleEntityUpdated = (
    payload: AgentEntityUpdatedPayload,
    onEntityUpdated?: (p: AgentEntityUpdatedPayload) => void,
  ) => {
    if (
      tab !== "profile" &&
      !payload.deleted &&
      payload.id != null &&
      selectedEntityId != null &&
      payload.id === selectedEntityId
    ) {
      setProfileDirty(true)
    }
    onEntityUpdated?.(payload)
  }

  return {
    tab,
    profileDirty,
    collapsed,
    setCollapsed,
    selectTab,
    handleEntityUpdated,
  }
}

/** Shared floating bottom dock for Canvas and entity Profile/Assistant panels. */
export function WorkspaceBottomDock({
  tabs,
  collapsed,
  onCollapsedChange,
  assistantActive,
  agent,
  children,
  toolbar,
  dirtyAriaLabel,
  aboveSlot,
  panelMountRef,
  rootRef,
  className,
}: WorkspaceBottomDockProps) {
  const sidebarCollapsed = useSidebarCollapsed(true)
  const drawerContentRight = useShellDrawerContentRight()
  const [assistantBusy, setAssistantBusy] = useState(false)
  const onBusyChangeRef = useRef(agent.onBusyChange)
  onBusyChangeRef.current = agent.onBusyChange
  const handleBusyChange = useCallback((busy: boolean) => {
    setAssistantBusy(busy)
    onBusyChangeRef.current?.(busy)
  }, [])

  const handleExpandedChange = useCallback(
    (open: boolean) => {
      onCollapsedChange(!open)
    },
    [onCollapsedChange],
  )

  // Keep both panels mounted and laid out: inactive uses opacity+absolute (not display:none /
  // visibility:hidden) so tab swaps skip remount/remeasure and avoid backdrop-filter flash on
  // primary buttons when the pane becomes visible again.
  const inactivePaneClass = "pointer-events-none absolute inset-x-0 bottom-0 opacity-0"

  return (
    <div
      ref={rootRef}
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-3 md:px-4 lg:px-6",
        sidebarCollapsed ? "left-0 md:left-14" : "left-0 md:left-[var(--shell-sidebar-width)]",
      )}
      style={{ right: drawerContentRight }}
    >
      <div
        ref={panelMountRef}
        className={cn(
          "pointer-events-none relative mx-auto flex w-full max-w-none min-w-0 flex-col gap-2 md:max-w-4xl",
          className,
        )}
      >
        {aboveSlot ? <div className="pointer-events-auto">{aboveSlot}</div> : null}

        <WorkspaceBottomPanelChrome
          dirtyAriaLabel={dirtyAriaLabel}
          assistantBusy={assistantBusy}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          toolbar={toolbar}
          tabs={tabs}
        >
          <div className="relative w-full">
            <div
              className={cn(WORKSPACE_BOTTOM_DOCK_CARD_CLASS, "pt-5", assistantActive ? inactivePaneClass : "relative")}
              aria-hidden={assistantActive}
              {...(assistantActive ? { inert: true } : {})}
            >
              {children({ collapsed })}
            </div>
            <div
              className={cn(!assistantActive ? inactivePaneClass : "relative")}
              aria-hidden={!assistantActive}
              {...(!assistantActive ? { inert: true } : {})}
            >
              <AgentChatPanel
                {...agent}
                onBusyChange={handleBusyChange}
                expanded={!collapsed}
                onExpandedChange={handleExpandedChange}
                showExpandToggle={false}
                collapseOnOutsideClick={false}
                anchor="inline"
                zIndexClass="z-40"
              />
            </div>
          </div>
        </WorkspaceBottomPanelChrome>
      </div>
    </div>
  )
}
